import {
    createDataMapper,
    mergeWithDefault,
    transformSourceToTargetKeys,
} from '@common/utils';
import { CLIP_KEYS } from '@features/Clip/constants';
import { CFDAPI_KEYS, DEFAULT_CFDAPI } from '../constants';
import {
    APICustomFieldDescription,
    APIFieldType,
    BackendCFD,
    FacetType,
    GetCfdAPIArgs,
} from '../types';
import {
    CLIP_FACET_REGISTRY,
    SUPPORTED_CFD_KEYS,
    SupportedCFDKeys,
} from '@features/advancedSearch/constants';

export const mapApiTypeToFacetType = (apiType: APIFieldType): FacetType => {
    switch (apiType) {
        case 'DATE':
            return 'date';
        case 'INTEGER':
        case 'LONG':
        case 'DOUBLE':
            return 'range';
        case 'BOOLEAN':
        case 'STRING':
        case 'TEXT':
        default:
            return 'boolean';
    }
};

export const getRegistryOverride = (key: string) =>
    CLIP_FACET_REGISTRY.find((config) => config.key === key);

/**
 * Maps a single backend BackendCFD item to the frontend APICustomFieldDescription model
 */
const mapBackendCFDToFrontend = (
    raw: BackendCFD,
): APICustomFieldDescription[] => {
    const keyMap = CLIP_KEYS as unknown as Record<string, string>;

    const reverseKeyMap = Object.entries(keyMap).reduce(
        (acc, [feKey, beKey]) => {
            acc[beKey] = feKey;
            return acc;
        },
        {} as Record<string, string>,
    );

    return Object.entries(raw)
        .reduce((acc, [rawKey, rawFieldData]) => {
            const possibleBeKeys = [`cf_s_value_${rawKey}`, rawKey];

            const foundKey = possibleBeKeys.find((k) => reverseKeyMap[k]);
            const key = foundKey ? reverseKeyMap[foundKey] : undefined;

            if (!key || !SUPPORTED_CFD_KEYS.includes(key as SupportedCFDKeys))
                return acc;

            const override = getRegistryOverride(key);
            const transformed = transformSourceToTargetKeys(
                rawFieldData,
                CFDAPI_KEYS,
            );
            const type =
                override?.type || mapApiTypeToFacetType(rawFieldData.type);

            acc.push(
                mergeWithDefault(
                    {
                        ...transformed,
                        key,
                        label: key,
                        type,
                        ...override,
                    },
                    DEFAULT_CFDAPI,
                ),
            );

            return acc;
        }, [] as APICustomFieldDescription[])
        .sort((a, b) => (a.order || 99) - (b.order || 99));
};

/**
 * Data mapper for APICustomFieldDescription (handles arrays)
 */
export const transformAPICustomFieldDescription = createDataMapper<
    BackendCFD,
    APICustomFieldDescription[],
    GetCfdAPIArgs
>(mapBackendCFDToFrontend, false);
